import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, Check, X, ChevronDown, Plus } from "lucide-react";

/**
 * Searchable, filterable service picker for the quote builder.
 * Replaces the old long-scroll card list with a single combobox.
 *
 * Props:
 *   services         : full services list (already fetched)
 *   selectedLabels   : Array<string> of currently-selected service labels (from line_items)
 *   onToggle(service): called when user clicks a row to add/remove
 *   onAddCustom()    : called when user clicks "Add custom line item" (optional)
 *   disabled         : disable picker (e.g. when configurable estimate is active)
 */
export const ServicePicker = ({ services, selectedLabels = [], onToggle, onAddCustom, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("all"); // all | public | internal
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  const baseServices = useMemo(
    () => services.filter((s) => s.active && !s.is_modifier),
    [services]
  );

  const counts = useMemo(
    () => ({
      all: baseServices.length,
      public: baseServices.filter((s) => s.public).length,
      internal: baseServices.filter((s) => !s.public).length,
    }),
    [baseServices]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return baseServices.filter((s) => {
      if (tab === "public" && !s.public) return false;
      if (tab === "internal" && s.public) return false;
      if (!q) return true;
      const hay = `${s.label || ""} ${s.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [baseServices, tab, query]);

  const selectedServices = useMemo(
    () => baseServices.filter((s) => selectedLabels.includes(s.label)),
    [baseServices, selectedLabels]
  );

  const handleToggle = (service) => {
    if (disabled) return;
    onToggle(service);
  };

  const triggerLabel =
    selectedServices.length === 0
      ? "Search or pick services..."
      : `${selectedServices.length} service${selectedServices.length === 1 ? "" : "s"} selected`;

  return (
    <div className="space-y-2" ref={wrapperRef}>
      <label className="text-sm font-medium text-slate-700">Services</label>

      {/* Selected pills (above the picker) */}
      {selectedServices.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="service-picker-selected-pills">
          {selectedServices.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-slate-900 border border-blue-200"
            >
              <span className="max-w-[180px] truncate">{s.label}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleToggle(s)}
                  className="hover:bg-blue-100 rounded-full p-0.5 -mr-1"
                  data-testid={`service-pill-remove-${s.id}`}
                  aria-label={`Remove ${s.label}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Picker trigger */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          data-testid="service-picker-trigger"
          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm rounded-md border transition-colors ${
            disabled
              ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
              : open
              ? "bg-white border-blue-800 ring-2 ring-blue-800/20"
              : "bg-white border-slate-300 hover:border-slate-400 text-slate-700"
          }`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Search className={`w-4 h-4 ${selectedServices.length > 0 ? "text-blue-800" : "text-slate-400"}`} />
            <span className={`truncate ${selectedServices.length === 0 ? "text-slate-500" : "text-slate-900 font-medium"}`}>
              {triggerLabel}
            </span>
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {/* Dropdown panel */}
        {open && !disabled && (
          <div
            className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
            data-testid="service-picker-dropdown"
          >
            {/* Search input */}
            <div className="p-2 border-b border-slate-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search services..."
                  data-testid="service-picker-search"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:border-blue-800 focus:ring-2 focus:ring-blue-800/20"
                />
              </div>

              {/* Category tabs */}
              {counts.internal > 0 && counts.public > 0 && (
                <div className="flex bg-slate-100 rounded-md p-0.5 mt-2 text-xs">
                  {[
                    { id: "all", label: "All", n: counts.all },
                    { id: "public", label: "Customer-Facing", n: counts.public },
                    { id: "internal", label: "Internal", n: counts.internal },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      data-testid={`service-picker-tab-${t.id}`}
                      className={`flex-1 px-2 py-1.5 rounded transition-colors font-medium ${
                        tab === t.id
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                    >
                      {t.label} <span className="text-slate-400">({t.n})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Results list */}
            <div className="max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-slate-500 mb-3">
                    {query ? `No services match "${query}"` : "No services in this category"}
                  </p>
                  {onAddCustom && (
                    <button
                      type="button"
                      onClick={() => {
                        onAddCustom();
                        setOpen(false);
                      }}
                      data-testid="service-picker-add-custom-empty"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-900 text-white rounded-md hover:bg-slate-900 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Create custom line item
                    </button>
                  )}
                </div>
              ) : (
                <ul className="py-1">
                  {filtered.map((s) => {
                    const isAdded = selectedLabels.includes(s.label);
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => handleToggle(s)}
                          data-testid={`service-picker-row-${s.id}`}
                          className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                            isAdded ? "bg-blue-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div
                            className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${
                              isAdded ? "bg-slate-900 border-slate-900" : "border-slate-300 bg-white"
                            }`}
                          >
                            {isAdded && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-900">{s.label}</span>
                              {!s.public && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-medium uppercase tracking-wide">
                                  Internal
                                </span>
                              )}
                            </div>
                            {s.description && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{s.description}</div>
                            )}
                          </div>
                          {s.price_value > 0 && (
                            <span className="text-sm font-semibold text-slate-900 flex-shrink-0">
                              ${s.price_value}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Footer: persistent add-custom + done */}
            <div className="border-t border-slate-200 p-2 flex items-center justify-between gap-2 bg-slate-50">
              {onAddCustom ? (
                <button
                  type="button"
                  onClick={() => {
                    onAddCustom();
                    setOpen(false);
                  }}
                  data-testid="service-picker-add-custom"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-900 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Custom line item
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="service-picker-done"
                className="px-3 py-1.5 text-xs font-medium bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {disabled && (
        <p className="text-xs text-amber-600">Preset services disabled — using configurable estimate</p>
      )}
    </div>
  );
};

export default ServicePicker;
